import { UserContract } from "#/api/contracts.type";
import Typography from "#/components/shared/typography";
import { formatTimeStampToHumanReadableShortDateTime } from "#/components/utls/time";
import { extractPatientNameFromContract } from "#/lib/contractFormValues";

interface MedicalRecordsRequestListItemProps {
  request: UserContract;
  index: number;
}

const getLabel = (request: UserContract, index: number) => {
  const providerName = request.signing?.providerSummary?.name;
  if (providerName) return providerName;
  return `Request ${index + 1}`;
};

const getStatus = (request: UserContract) => request.signing?.status ?? "pending";

const getTimestamp = (request: UserContract) =>
  request.signing?.updated_at || request.signing?.created_at || null;

const getSignedUrl = (request: UserContract) =>
  request.signedPdfUrl || request.signedDocument?.signed_url || undefined;

export function MedicalRecordsRequestListItem({
  request,
  index,
}: MedicalRecordsRequestListItemProps) {
  const label = getLabel(request, index);
  const status = getStatus(request);
  const timestamp = getTimestamp(request);
  const pdfUrl = getSignedUrl(request);
  const patientName = extractPatientNameFromContract(
    request as unknown as Record<string, any>
  );

  return (
    <li className="p-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <Typography variant="subtitle1" className="text-brown-900">
            {label}
          </Typography>
          {patientName ? (
            <Typography variant="body2" className="text-brown-700">
              Patient: {patientName}
            </Typography>
          ) : null}
          <Typography variant="body2" className="text-brown-600">
            Status: {status}
          </Typography>
          {timestamp ? (
            <Typography variant="caption" className="text-brown-500">
              Updated {formatTimeStampToHumanReadableShortDateTime(timestamp)}
            </Typography>
          ) : null}
        </div>
        {pdfUrl ? (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-lg border border-cream-400 bg-cream-50 px-3 py-1 text-sm font-medium text-brown-700 transition-colors hover:bg-cream-100"
          >
            View PDF
          </a>
        ) : null}
      </div>
    </li>
  );
}

export default MedicalRecordsRequestListItem;
